import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';

import { TeamManager } from '@/components/team-manager';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

interface UserInfo {
  email?: string | undefined;
  name?: string | undefined;
}

export default function TeamPage(): import('react/jsx-runtime').JSX.Element {
  const { user } = useAuth();

  const { data: membershipsRes, isLoading } = useQuery({
    queryKey: ['org-memberships'],
    queryFn: () => api.getOrgMemberships(),
    staleTime: 5 * 60 * 1000,
  });

  const activeOrgId = user?.primaryOrganization?.id ?? null;
  const memberships = membershipsRes?.orgMemberships ?? [];

  const orgEntry =
    activeOrgId != null
      ? memberships.find((m): boolean => m.org?.id === activeOrgId)
      : memberships[0];

  const org =
    orgEntry?.org?.id != null ? { id: orgEntry.org.id, name: orgEntry.org.name ?? '' } : null;

  const { data: membersRes } = useQuery({
    queryKey: ['org-members', org?.id],
    queryFn: () => api.getOrgMembers(org!.id),
    enabled: org != null,
    staleTime: 60 * 1000,
  });

  const { data: projectsRes } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    staleTime: 60 * 1000,
  });

  const members = membersRes?.orgMembers ?? [];
  const orgProjects = (projectsRes?.projects ?? []).filter(
    (p): boolean => p.organization?.id === org?.id && p.id != null,
  );

  const { data: usersData } = useQuery({
    queryKey: ['users-for-team', members.map((m) => m.userId).join(',')],
    queryFn: async () => {
      const ids = members.map((m): string => m.userId ?? '').filter(Boolean);
      if (ids.length === 0) return { usersByIds: [] };
      return api.getUsersByIds(ids);
    },
    enabled: members.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  if (!org) return <Navigate to="/profile" replace />;

  const myRole = orgEntry?.role ?? null;

  const userMap = new Map<string, UserInfo>();
  for (const u of usersData?.usersByIds ?? []) {
    if (u.id != null) userMap.set(u.id, { email: u.email ?? undefined, name: u.name ?? undefined });
  }

  const userId = user?.id ?? '';

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">
          {org.name}
        </p>
        <h1 className="text-lg font-semibold">Team members</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Invite members to the whole organization or grant access to specific apps only.
        </p>
      </div>

      <TeamManager
        orgId={org.id}
        orgName={org.name}
        currentUserId={userId}
        myRole={myRole ?? 'viewer'}
        initialMembers={members.map((m) => {
          const uInfo = userMap.get(m.userId ?? '') ?? {};
          return {
            id: m.id ?? '',
            userId: m.userId ?? '',
            email: uInfo.email,
            name: uInfo.name,
            role: m.role ?? 'viewer',
            joinedAt: m.joinedAt ?? new Date(0).toISOString(),
            scope: 'org' as const,
          };
        })}
        orgProjects={orgProjects.map((p) => ({ id: p.id!, name: p.name ?? '' }))}
        initialProjectMembers={[]}
      />
    </div>
  );
}
